import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Button from "Components/Common/Button/Button";
import { useToast } from "Components/Common/Toast/ToastContext";
import PublicPageShell, { PublicPageStatus } from "./PublicPageShell";
import {
  getPreconReviewByToken,
  submitPreconDecision,
} from "services/publicService";

interface PreconReviewData {
  projectName?: string;
  [key: string]: unknown;
}

const PreconReview = () => {
  const { token = "" } = useParams<{ token: string }>();
  const showToast = useToast();
  const [status, setStatus] = useState<PublicPageStatus>("loading");
  const [data, setData] = useState<PreconReviewData | null>(null);
  const [decided, setDecided] = useState<"approve" | "reject" | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    getPreconReviewByToken(token)
      .then((res) => {
        if (cancelled) return;
        setData(res.data?.data ?? res.data ?? null);
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleDecision = async (decision: "approve" | "reject") => {
    setSubmitting(true);
    try {
      await submitPreconDecision(token, decision);
      setDecided(decision);
      showToast(
        decision === "approve"
          ? "Pre-construction report approved."
          : "Pre-construction report rejected.",
      );
    } catch {
      showToast("Failed to submit your decision. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicPageShell
      title="Pre-Construction Report Review"
      status={status}
    >
      {decided ? (
        <div className="ppCard">
          <div className="ppCardTitle">
            {decided === "approve" ? "Approved" : "Rejected"}
          </div>
          <p className="ppNote">
            Thank you — your decision has been recorded. You may close this
            page.
          </p>
        </div>
      ) : (
        <div className="ppCard">
          <div className="ppCardTitle">
            {data?.projectName ?? "Pre-Construction Report"}
          </div>
          <p className="ppNote">
            Review the pre-construction report for this project, then approve
            or reject below.
          </p>
          <div className="ppActions">
            <Button
              className="btn btnSecondary"
              onClick={() => handleDecision("reject")}
              disabled={submitting}
            >
              Reject
            </Button>
            <Button
              className="btn btnPrimary"
              onClick={() => handleDecision("approve")}
              disabled={submitting}
            >
              Approve
            </Button>
          </div>
        </div>
      )}
    </PublicPageShell>
  );
};

export default PreconReview;
